import React, { useState, useEffect } from 'react';
import api from '../api';
import { Search, Edit2, Check, X, AlertTriangle } from 'lucide-react';

const StockAdjustments = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    try {
      const data = await api.getInventory();
      setProducts(Array.isArray(data) ? data : data.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const filtered = products.filter(p => {
    const q = searchQuery.toLowerCase();
    return !q || (p.product_name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q);
  });

  const startEdit = (p) => {
    setEditingId(p.id || p.variant_id);
    setEditQty(String(p.quantity_on_hand || 0));
  };

  const cancelEdit = () => { setEditingId(null); setEditQty(''); };

  const saveQty = async (p) => {
    setSaving(true);
    try {
      const newQty = parseInt(editQty) || 0;
      await api.request(`/api/inventory/${p.id || p.variant_id}/adjust`, {
        method: 'PUT',
        body: JSON.stringify({ quantity: newQty, reason: 'Manual stock adjustment' }),
      });
      setProducts(prev => prev.map(pr =>
        (pr.id || pr.variant_id) === (p.id || p.variant_id) ? { ...pr, quantity_on_hand: newQty } : pr
      ));
      setEditingId(null);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="stock-adjustments-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Stock Adjustments</h1>
        <div className="text-sm text-muted-foreground">{filtered.length} products</div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input type="text" placeholder="Search by name or SKU..."
          value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-input rounded-lg text-sm" data-testid="stock-search" />
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="stock-table">
            <thead>
              <tr className="bg-muted/30 border-b">
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Product</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">SKU</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Category</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Current Qty</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">No products found</td></tr>
              ) : filtered.map((p) => {
                const id = p.id || p.variant_id;
                const qty = p.quantity_on_hand || 0;
                const isEditing = editingId === id;
                const isLow = qty < 3;
                return (
                  <tr key={id} className={`hover:bg-muted/20 ${isLow ? 'bg-red-50/50' : ''}`} data-testid={`stock-row-${id}`}>
                    <td className="px-4 py-3 text-sm font-medium">{p.product_name || p.name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground font-mono">{p.sku || '—'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{p.category || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <input type="number" value={editQty} onChange={e => setEditQty(e.target.value)}
                          className="w-20 px-2 py-1 border border-input rounded text-sm text-right ml-auto block"
                          autoFocus onKeyDown={e => { if (e.key === 'Enter') saveQty(p); if (e.key === 'Escape') cancelEdit(); }}
                          data-testid="edit-qty-input" />
                      ) : (
                        <span className={`font-semibold tabular-nums ${isLow ? 'text-red-600' : qty === 0 ? 'text-red-500' : 'text-foreground'}`}>
                          {qty}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isLow && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                          <AlertTriangle className="h-3 w-3" /> Low
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isEditing ? (
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => saveQty(p)} disabled={saving}
                            className="p-1.5 rounded hover:bg-green-100 text-green-600" data-testid="save-qty-btn">
                            <Check className="h-4 w-4" />
                          </button>
                          <button onClick={cancelEdit} className="p-1.5 rounded hover:bg-muted" data-testid="cancel-qty-btn">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => startEdit(p)} className="p-1.5 rounded hover:bg-muted" data-testid={`edit-qty-${id}`}>
                          <Edit2 className="h-4 w-4 text-muted-foreground" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StockAdjustments;
